import _ from 'lodash';
import { splitAndConvert } from '@/shared/lib/splitAndConvert';
import { EMITTER_CONSTANTS } from '@/shared/config';
import emitter from '@/shared/lib/eventEmitter';
import { updateDataState } from './dbState';
import { createPerStudyDb, isPersistenceDisabledError } from './createPerStudyDb';
import { addData, getData, clearDataAfterIndex, keyExistsBeforeIndex } from './utils';

// TODO(ts): `value` wraps arbitrary Recoil atom state for undo/redo, not derivable
// from a GraphQL document or schema.
export type HistoryStateRecord = {
  key: number;
  value: {
    actionName: string;
    value: { newValue?: any; oldValue?: any; positions?: any };
  };
};

const NAME_DB_HISTORY = 'DB_STATE_HISTORY_RECOIL';
const NAME_OBJECT_STORE_HISTORY = 'STATE_HISTORY_RECOIL';
const KEY_FIELD_HISTORY = 'key';

const historyDb = createPerStudyDb({
  dbSuffix: NAME_DB_HISTORY,
  storeName: NAME_OBJECT_STORE_HISTORY,
  keyPath: KEY_FIELD_HISTORY,
});
let currentIndex = 0;
let maxIndex = 0;
let minIndex = 1;

const stateStorage: {
  defaultValue: Record<string, any>;
  defaultValueHeatmap: Record<string, any>;
  oldValueHeatmap: Record<string, any>;
} = {
  defaultValue: {},
  defaultValueHeatmap: {},
  oldValueHeatmap: {},
};

/**
 * Opens the per-profile undo and redo history database and starts it empty. Names it from the given key or the profile id fallback, caches the handle, clears the history object store, and logs then rethrows when the open fails.
 *
 * @param key - Study or profile id naming the history database; falls back to the profile id key.
 * @returns A promise resolving once the history database is open and its store cleared.
 */
const openDBHistoryState = async (key?: string) => {
  try {
    await historyDb.openDb(key);
    await historyDb.clearStore();
  } catch (error) {
    // A gated open before persistence is enabled is expected (nothing to open yet); don't log it as
    // an error. Real open failures still surface.
    if (!isPersistenceDisabledError(error)) {
      console.error(`[${NAME_DB_HISTORY}] Error opening database:`, error);
    }
    throw error;
  }
};

// function add data and return new index
/**
 * Appends one undo history action at the next index and advances the history cursor. Adds the record through addData at currentIndex plus one, then sets both currentIndex and maxIndex to the returned key.
 *
 * @param action - History action carrying the action name and its old and new values.
 * @returns A promise resolving once the action has been appended and the indices advanced.
 */
const processNewData = async (action: HistoryStateRecord['value']) => {
  const newIndex = (await historyDb.runWrite((db) =>
    addData(db, NAME_OBJECT_STORE_HISTORY, currentIndex + 1, action),
  )) as number;
  currentIndex = newIndex;
  maxIndex = newIndex;
};

/**
 * Records one field change in the undo history so it can later be undone or redone. Opens the history database when needed, chains the old value off the previous entry for the same key or seeds it from the captured default, truncates any redo tail past the current index, appends the action only when the new value differs, and emits the updated index pair; failures are logged and rethrown.
 *
 * @param key - Persistence key of the field that changed, used as the action name.
 * @param value - Change payload carrying oldValue and newValue.
 * @returns A promise resolving to null once the history entry has been processed.
 */
const addDataHistoryState = async (
  key: string,
  value: { oldValue?: any; newValue?: any },
): Promise<null> => {
  if (!historyDb.db) {
    await openDBHistoryState();
  }
  try {
    const oldData = await getData<HistoryStateRecord>(
      historyDb.db!,
      NAME_OBJECT_STORE_HISTORY,
      currentIndex,
    );
    if (oldData) {
      if (key === oldData.value?.actionName) {
        value.oldValue = oldData.value?.value?.newValue;
      }
    } else {
      const exists = await keyExistsBeforeIndex(
        historyDb.db!,
        NAME_OBJECT_STORE_HISTORY,
        key,
        currentIndex,
      );
      // If key does not exist, initialize defaultValue
      if (!exists) {
        if (!Object.prototype.hasOwnProperty.call(stateStorage.defaultValue, key)) {
          stateStorage.defaultValue[key] = value.oldValue;
        } else {
          value.oldValue = stateStorage.defaultValue[key];
        }
      }
    }

    // Xóa dữ liệu sau currentIndex để duy trì tính nhất quán lịch sử
    await historyDb.runWrite((db) =>
      clearDataAfterIndex(db, NAME_OBJECT_STORE_HISTORY, currentIndex),
    );
    const action = { actionName: key, value };
    // Nếu giá trị mới khác với giá trị cũ, thêm dữ liệu vào cơ sở dữ liệu
    if (!_.isEqual(value.newValue, value.oldValue)) {
      await processNewData(action);
    }
    // Phát ra sự kiện để cập nhật currentIndex và maxIndex cho chức năng undo/redo
    emitter.emit(EMITTER_CONSTANTS.UNDO_REDO_CURRENT_INDEX, { currentIndex, maxIndex });
  } catch (error) {
    console.error(`[${NAME_DB_HISTORY}] Error processing data:`, error);
    throw error;
  }
  return null;
};

// function handle undo
/**
 * Steps the undo history back one entry and restores its previous value. Opens the history database when needed, yields null when the cursor is already below the minimum index, otherwise reads the record at the current index, writes its oldValue back into persisted state, rewinds the cursor, and resolves with the key and type split out of the action name plus the swapped values and stored positions.
 *
 * @returns A promise resolving to the undone entry, or null when there is nothing to undo.
 */
const handleUndoGroupedByKey = async () => {
  if (!historyDb.db) {
    await openDBHistoryState();
  }

  if (currentIndex < minIndex) {
    return null;
  }
  const transaction = historyDb.db!.transaction([NAME_OBJECT_STORE_HISTORY], 'readonly');
  const store = transaction.objectStore(NAME_OBJECT_STORE_HISTORY);

  return new Promise((resolve, reject) => {
    const request = store.get(currentIndex);
    request.onsuccess = (event) => {
      const data = (event.target as IDBRequest<HistoryStateRecord>).result;
      if (data && data.value) {
        updateDataState(data.value.actionName, data.value.value.oldValue);
        currentIndex -= 1;
        resolve({
          key: (splitAndConvert(data.value.actionName) as (string | number)[])[0],
          value: {
            value: data.value.value.newValue,
            valueOther: data.value.value.oldValue,
            positions: data.value.value.positions,
          },
          type: (splitAndConvert(data.value.actionName) as (string | number)[])[1],
          index: currentIndex,
          maxIndex,
        });
      } else {
        resolve(null);
      }
    };
    request.onerror = (event) => {
      console.error(`[${NAME_DB_HISTORY}]Request error`, (event.target as IDBRequest).error);
      reject((event.target as IDBRequest).error);
    };
  });
};

// function handle redo
/**
 * Steps the undo history forward one entry and reapplies its newer value. Opens the history database when needed, yields null when already at the newest entry, otherwise reads the record after the current index, writes its newValue back into persisted state, advances the cursor, and resolves with the key and type split out of the action name plus the swapped values and stored positions.
 *
 * @returns A promise resolving to the redone entry, or null when there is nothing to redo.
 */
const handleRedoGroupedByKey = async () => {
  if (!historyDb.db) {
    await openDBHistoryState();
  }

  if (currentIndex >= maxIndex) {
    return null;
  }

  const transaction = historyDb.db!.transaction([NAME_OBJECT_STORE_HISTORY], 'readonly');
  const store = transaction.objectStore(NAME_OBJECT_STORE_HISTORY);

  return new Promise((resolve, reject) => {
    const request = store.get(currentIndex + 1);
    request.onsuccess = (event) => {
      const data = (event.target as IDBRequest<HistoryStateRecord>).result;
      if (data && data.value) {
        updateDataState(data.value.actionName, data.value.value.newValue);
        currentIndex += 1;
        resolve({
          key: (splitAndConvert(data.value.actionName) as (string | number)[])[0],
          value: {
            value: data.value.value.oldValue,
            valueOther: data.value.value.newValue,
            positions: data.value.value.positions,
          },
          type: (splitAndConvert(data.value.actionName) as (string | number)[])[1],
          index: currentIndex,
          maxIndex,
        });
      } else {
        resolve(null);
      }
    };
    request.onerror = (event) => {
      console.error(`[${NAME_DB_HISTORY}]Request error`, (event.target as IDBRequest).error);
      reject((event.target as IDBRequest).error);
    };
  });
};

// functions for reset history flag undo/redo
/**
 * Wipes the undo and redo history back to its initial state. Opens the history database when needed, then after a one second delay clears the captured default and heatmap snapshots, resets the current, max and min indices, emits the refreshed index pair, and clears the history object store.
 *
 * @returns A promise resolving to undefined once the reset has been scheduled.
 */
const resetHistoryState = async (): Promise<undefined> => {
  // A reset can be requested before the report fetch enables persistence; the per-study open is
  // gated and rejects then. Nothing is stored yet in that case, so treat it as a no-op instead of
  // surfacing an uncaught rejection.
  if (!historyDb.db) {
    try {
      await openDBHistoryState();
    } catch {
      return undefined;
    }
  }
  try {
    setTimeout(() => {
      stateStorage.defaultValue = {};
      stateStorage.oldValueHeatmap = {};
      currentIndex = 0;
      maxIndex = 0;
      minIndex = 1;
      emitter.emit(EMITTER_CONSTANTS.UNDO_REDO_CURRENT_INDEX, { currentIndex, maxIndex });
      historyDb.clearStore().catch((error) => {
        console.error(`[${NAME_DB_HISTORY}] Error clearing history store on reset:`, error);
      });
    }, 1000);
  } catch (error) {
    console.error(`[${NAME_DB_HISTORY}] Error resetting history state:`, error);
    return Promise.reject(error);
  }
  return undefined;
};

/**
 * Drops the per-study undo and redo history database. Builds the name from the study id and the history database suffix, then deletes it through deleteDatabase.
 *
 * @param studyId - Study whose history database is removed.
 * @returns A promise resolving once the history database has been deleted.
 */
const deleteDataFromDBHistoryState = async (studyId: string) => {
  await historyDb.removeDb(studyId);
};

export {
  openDBHistoryState,
  addDataHistoryState,
  handleUndoGroupedByKey,
  handleRedoGroupedByKey,
  resetHistoryState,
  deleteDataFromDBHistoryState,
};
